import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucidePlay } from '@ng-icons/lucide';
import { BrnDialogDescription, BrnDialogRef, BrnDialogTitle } from '@spartan-ng/brain/dialog';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import {
  DIALOG_CARD_LEAD_CLASS,
  DIALOG_CARD_TITLE_CLASS,
  DialogSheetComponent,
  dialogSheetHostClass,
} from '@lite-workspace/shared/helm-ui/dialog';
import { PLAY_DISC } from './coach-card.component';

/** Итог окна: «Смотреть видео» — единственный; «Позже», клик мимо и Esc закрывают его без итога. */
export type WelcomeVideoResult = 'watch';

const WELCOME_COVER = '/assets/illustrations/onboarding-welcome-cover.jpg';

/**
 * Приветствие новичка: одна обложка, два ответа. Карточка та же, что у окна связи с поддержкой в
 * Telegram, только без крестика — окно закрывают сами ответы и клик мимо.
 */
@Component({
  selector: 'onboarding-welcome-video-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIcon, HlmButton, DialogSheetComponent, BrnDialogTitle, BrnDialogDescription],
  viewProviders: [provideIcons({ lucidePlay })],
  host: { '[class]': 'hostClass' },
  styles: `
    /* Диск проигрывания перекрашивает фон и обводку и растёт на наведении — как на карточке коуча. */
    :host {
      --coach-t-props: background-color, border, transform;
    }
  `,
  template: `
    <hlm-dialog-sheet>
      <ng-template>
        <h2 brnDialogTitle [class]="titleClass">Видео обзор сервиса</h2>
        <p brnDialogDescription [class]="leadClass">Короткие мини-видео для быстрого знакомства с Люмиром.</p>

        <button
          type="button"
          class="group relative mb-6 block aspect-video w-full overflow-hidden rounded-[var(--r-m)] border border-line2
                 bg-stage-black"
          aria-label="Смотреть видео"
          (click)="watch()"
        >
          <img [src]="cover" alt="" class="absolute inset-0 size-full object-cover" />
          <span aria-hidden="true" class="absolute inset-0 bg-linear-to-b from-stage-black/20 to-stage-black/50"></span>

          <span aria-hidden="true" [class]="playDisc">
            <ng-icon name="lucidePlay" class="text-catalog-title [&>svg]:fill-current" />
          </span>
        </button>

        <div class="grid grid-cols-2 gap-3">
          <button hlmBtn type="button" variant="outline" class="min-h-12.5 justify-center" (click)="later()">Позже</button>
          <button hlmBtn type="button" class="min-h-12.5 justify-center" (click)="watch()">Смотреть видео</button>
        </div>
      </ng-template>
    </hlm-dialog-sheet>
  `,
})
export class WelcomeVideoDialogComponent {
  private readonly dialogRef = inject<BrnDialogRef<WelcomeVideoResult | undefined>>(BrnDialogRef);

  // Окно всегда тёмное, как карточка коуча: обложка, диск и его наведение одинаковы в обеих темах.
  protected readonly hostClass = `dark relative text-ink ${dialogSheetHostClass('bonus', 'bonus')}`;
  protected readonly titleClass = DIALOG_CARD_TITLE_CLASS;
  protected readonly leadClass = DIALOG_CARD_LEAD_CLASS;
  protected readonly cover = WELCOME_COVER;
  protected readonly playDisc = PLAY_DISC;

  protected watch(): void {
    this.dialogRef.close('watch');
  }

  protected later(): void {
    this.dialogRef.close(undefined);
  }
}
